import { collection, addDoc, getDocs, orderBy, query, limit, serverTimestamp, deleteDoc, doc } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { db, storage } from '../config/firebase'

export type SharedMaterial = {
  id?: string
  name: string
  type: string
  size: number
  url: string
  storagePath: string
  uploadedBy: string
  createdAt?: unknown
}

class MaterialsService {
  private collectionName = 'sharedMaterials'

  async uploadSharedMaterial(file: File, uploadedBy: string): Promise<SharedMaterial> {
    const storagePath = `shared-materials/${Date.now()}-${file.name}`
    const storageRef = ref(storage, storagePath)

    // 1) Upload to storage
    const snapshot = await uploadBytes(storageRef, file)

    // 2) Get download URL
    const url = await getDownloadURL(snapshot.ref)

    // 3) Save metadata in Firestore
    const payload = {
      name: file.name,
      type: file.type || 'application/octet-stream',
      size: file.size,
      url,
      storagePath,
      uploadedBy,
      createdAt: serverTimestamp(),
    }

    const docRef = await addDoc(collection(db, this.collectionName), payload)
    return { id: docRef.id, ...(payload as Omit<SharedMaterial, 'id'>) }
  }

  async listSharedMaterials(limitCount: number = 50): Promise<SharedMaterial[]> {
    const q = query(collection(db, this.collectionName), orderBy('createdAt', 'desc'), limit(limitCount))
    const snap = await getDocs(q)
    return snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as SharedMaterial[]
  }

  async deleteSharedMaterial(id: string, storagePath?: string): Promise<void> {
    // Remove Firestore doc
    await deleteDoc(doc(db, this.collectionName, id))

    // Try to remove storage object (optional)
    if (storagePath) {
      try {
        const objectRef = ref(storage, storagePath)
        await deleteObject(objectRef)
      } catch (e) {
        // Swallow errors for storage delete to avoid blocking UI
        console.warn('Storage delete warning:', e)
      }
    }
  }
}

export default new MaterialsService()
